import placeholders from './thinking-placeholders.json';

const DEFAULT_THINKING_PLACEHOLDER = 'Thinking';

const THINKING_PLACEHOLDERS = Object.freeze(
  placeholders
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0),
);

function isValidPlaceholderIndex(index: number, count: number): boolean {
  return Number.isInteger(index) && index >= 0 && index < count;
}

export interface ThinkingPlaceholderRotationState {
  currentIndex: number;
  remainingIndices: number[];
}

export function createThinkingPlaceholderRotationState(): ThinkingPlaceholderRotationState {
  return { currentIndex: -1, remainingIndices: [] };
}

export function resolveThinkingPlaceholder(index: number): string {
  const count = THINKING_PLACEHOLDERS.length;
  if (count === 0) return DEFAULT_THINKING_PLACEHOLDER;
  if (!isValidPlaceholderIndex(index, count)) {
    return THINKING_PLACEHOLDERS[0] ?? DEFAULT_THINKING_PLACEHOLDER;
  }
  return THINKING_PLACEHOLDERS[index] ?? DEFAULT_THINKING_PLACEHOLDER;
}

function refillRemainingIndices(state: ThinkingPlaceholderRotationState, count: number): void {
  const nextIndices: number[] = [];
  for (let index = 0; index < count; index++) {
    if (count > 1 && index === state.currentIndex) continue;
    nextIndices.push(index);
  }
  state.remainingIndices = nextIndices;
}

export function pickNextThinkingPlaceholder(
  state: ThinkingPlaceholderRotationState,
  random: () => number = Math.random,
): { index: number; text: string } {
  const count = THINKING_PLACEHOLDERS.length;
  if (count === 0) {
    state.currentIndex = -1;
    state.remainingIndices = [];
    return { index: -1, text: DEFAULT_THINKING_PLACEHOLDER };
  }
  if (count === 1) {
    state.currentIndex = 0;
    state.remainingIndices = [];
    return { index: 0, text: THINKING_PLACEHOLDERS[0] ?? DEFAULT_THINKING_PLACEHOLDER };
  }

  if (!isValidPlaceholderIndex(state.currentIndex, count)) {
    state.currentIndex = -1;
  }
  state.remainingIndices = state.remainingIndices.filter((index) => isValidPlaceholderIndex(index, count));
  if (state.remainingIndices.length === 0) {
    refillRemainingIndices(state, count);
  }

  const pickPosition = Math.floor(random() * state.remainingIndices.length);
  const [index = 0] = state.remainingIndices.splice(pickPosition, 1);
  state.currentIndex = index;

  return { index, text: THINKING_PLACEHOLDERS[index] ?? DEFAULT_THINKING_PLACEHOLDER };
}
